<template>
  <div class="h-full">
    <!-- Mobile sidebar -->
    <div 
      v-show="isOpen" 
      class="fixed inset-0 z-50 lg:hidden"
      @click="$emit('close')"
    >
      <div class="fixed inset-0 bg-background/80 backdrop-blur-sm transition-opacity"></div>
    </div>

    <!-- Sidebar content -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-border shadow-large transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:relative lg:flex-shrink-0 h-full flex flex-col theme-transition"
      :class="isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'"
    >
      <!-- Sidebar header -->
      <div class="flex items-center justify-between h-16 px-6 border-b border-border">
        <div class="flex items-center">
          <!-- Light mode logo -->
          <img 
            src="@/assets/main.png" 
            alt="Parking System Logo" 
            class="w-full h-full object-contain block dark:hidden"
          />
          <!-- Dark mode logo -->
          <img 
            src="@/assets/main-light.png" 
            alt="Parking System Logo" 
            class="w-full h-full object-contain hidden dark:block"
          />
        </div>
        
        <!-- Close button for mobile -->
        <button 
          @click="$emit('close')"
          class="lg:hidden p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary theme-transition"
          aria-label="Close sidebar"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Navigation menu -->
      <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto min-h-0 scrollbar-custom">
        <div v-for="section in navigationSections" :key="section.name" class="space-y-1">
          <!-- Section header -->
          <h3 class="px-3 text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2 mt-4">
            {{ section.name }} 
          </h3>
          
          <!-- Section items -->
          <div class="space-y-1">
            <router-link
              v-for="item in section.items"
              :key="item.name"
              :to="item.path"
              :class="[
                'nav-item group flex items-center px-3 py-2.5 text-sm font-bold rounded-lg transition-all duration-200',
                isActive(item.path)
                  ? 'nav-item-active text-primary-700 dark:text-primary'
                  : 'text-text-secondary hover:text-primary-700 dark:hover:text-primary nav-item-hover'
              ]"
              @click="$emit('navigate')"
            >
              <component 
                :is="item.icon" 
                :class="[
                  'mr-3 h-5 w-5 transition-colors duration-200',
                  isActive(item.path)
                    ? 'text-primary-700 dark:text-primary'
                    : 'text-text-muted group-hover:text-primary-700 dark:group-hover:text-primary'
                ]"
              />
              {{ item.name }}
              
              <!-- Badge for notifications -->
              <span 
                v-if="item.badge"
                class="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                :class="item.badge.type === 'error' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary-700 dark:text-primary'"
              >
                {{ item.badge.count }}
              </span>
            </router-link>
          </div>
        </div>

        <!-- Sidebar footer (Moved inside nav for scrolling) -->
        <div class="pt-4 mt-6 border-t border-border/50">
          <div class="space-y-1">
            <!-- Exit Contractor View Mode button -->
            <button
              v-if="isContractorViewMode"
              @click="handleExitContractorView"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-success rounded-lg hover:bg-success/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Exit Contractor View
            </button>
            
            <!-- Exit Company View Mode button -->
            <button
              v-if="authStore.isCompanyViewMode"
              @click="handleExitCompanyView"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-primary-700 dark:text-primary rounded-lg hover:bg-primary/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Exit Company View
            </button>
            
            <!-- Settings link -->
            <router-link
              to="/app/settings"
              class="flex items-center px-3 py-2 text-sm font-bold text-text-secondary rounded-lg hover:bg-surface-hover hover:text-text-primary transition-colors duration-200"
              @click="$emit('navigate')"
            >
              <Cog6ToothIcon class="mr-3 h-5 w-5 text-text-muted" />
              Settings
            </router-link>
            
            <!-- Logout button -->
            <button
              @click="handleLogout"
              class="w-full flex items-center px-3 py-2 text-sm font-bold text-danger rounded-lg hover:bg-danger/5 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 opacity-70" />
              Logout
            </button>
          </div>
          
          <!-- Version info -->
          <div class="mt-4 px-3">
            <p class="text-[10px] text-text-muted text-center font-bold uppercase tracking-widest">
              v1.0.0
            </p>
          </div>
        </div>
      </nav>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useToast } from 'vue-toastification'
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  TruckIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  ClipboardDocumentListIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  RectangleStackIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'navigate'])

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Computed
const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  const { firstName, lastName } = authStore.user
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase()
})

const isContractorViewMode = computed(() => {
  try {
    const stored = localStorage.getItem('contractorViewMode')
    return !!stored
  } catch {
    return false
  }
})

const navigationSections = computed(() => {
  // Get actual user role (not the computed one which might be overridden)
  const actualUserRole = authStore.userRole
  const userRole = authStore.userRole // Computed role (may be overridden in view modes)
  const isCompanyView = authStore.isCompanyViewMode
  const companyViewData = authStore.companyViewMode
  
  // Check if in contractor view mode
  const isContractorView = (() => {
    try {
      const stored = localStorage.getItem('contractorViewMode')
      return !!stored
    } catch {
      return false
    }
  })()
  
  // If logged in user is "Contractor" and in contractor view mode, show contractor-specific navigation
  if (isContractorView && actualUserRole === 'Contractor') {
    return [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          },
          {
            name: 'Users',
            path: '/app/users',
            icon: UsersIcon
          },
          {
            name: 'Locations',
            path: '/app/locations',
            icon: MapPinIcon
          },
          {
            name: 'Transactions',
            path: '/app/transactions',
            icon: ChartBarIcon
          },
          {
            name: 'Tariffs',
            path: '/app/tariffs',
            icon: CreditCardIcon
          },
          {
            name: 'Devices',
            path: '/app/devices',
            icon: DevicePhoneMobileIcon
          }
        ]
      }
    ]
  }
  
  // If logged in user is "Administrator" and in company view mode, show company-specific navigation
  if (isCompanyView && companyViewData && actualUserRole === 'Administrator') {
    const hasContractors = companyViewData.settings?.hasContractors === true || companyViewData.hasContractors === true
    
    const items = [
      {
        name: 'Dashboard',
        path: '/app/dashboard',
        icon: HomeIcon
      },
      {
        name: 'Users',
        path: '/app/users',
        icon: UsersIcon
      }
    ]
    
    // Add Contractors if hasContractors is true
    if (hasContractors) {
      items.push({
        name: 'Contractors',
        path: '/app/contractors',
        icon: UsersIcon
      })
    }
    
    // Add Transactions, Locations, Tariffs, Devices, NFC
    items.push(
      {
        name: 'Transactions',
        path: '/app/transactions',
        icon: ChartBarIcon
      },
      {
        name: 'Locations',
        path: '/app/locations',
        icon: MapPinIcon
      },
      {
        name: 'Tariffs',
        path: '/app/tariffs',
        icon: CreditCardIcon
      },
      {
        name: 'NFC Cards',
        path: '/app/nfc-cards',
        icon: IdentificationIcon
      },
      {
        name: 'NFC Subscriptions',
        path: '/app/card-subscriptions',
        icon: CreditCardIcon
      },
      {
        name: 'Devices',
        path: '/app/devices',
        icon: DevicePhoneMobileIcon
      }
    )
    
    return [
      {
        name: 'Main',
        items: items
      }
    ]
  }
  
  // Original navigation logic for normal mode
  
  // Define navigation items based on user role
  let sections = []
  
  if (userRole === 'SuperAdmin') {
    sections = [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          }
        ]
      },
      {
        name: 'Management',
        items: [
          {
            name: 'Users',
            path: '/app/users',
            icon: UsersIcon
          }
        ]
      },
      {
        name: 'Accounting',
        items: [
          {
            name: 'Accounting Dashboard',
            path: '/app/accounting',
            icon: CalculatorIcon
          },
          {
            name: 'Suppliers',
            path: '/app/accounting/suppliers',
            icon: BuildingStorefrontIcon
          },
          {
            name: 'Stock Items',
            path: '/app/accounting/stock-items',
            icon: CubeIcon
          },
          {
            name: 'Stock Batches',
            path: '/app/accounting/stock-batches',
            icon: RectangleStackIcon
          },
          {
            name: 'Purchase Orders',
            path: '/app/accounting/purchase-orders',
            icon: ShoppingCartIcon
          },
          {
            name: 'Quotations',
            path: '/app/accounting/quotations',
            icon: DocumentTextIcon
          },
          {
            name: 'Invoices',
            path: '/app/accounting/invoices',
            icon: ReceiptPercentIcon
          },
          {
            name: 'Payment Plans',
            path: '/app/accounting/payment-plans',
            icon: DocumentTextIcon
          },
          {
            name: 'Tax Settings',
            path: '/app/accounting/tax-settings',
            icon: CalculatorIcon
          },
          {
            name: 'Quality Standards',
            path: '/app/accounting/quality-standards',
            icon: BeakerIcon
          },
          {
            name: 'Stock Adjustments',
            path: '/app/accounting/stock-adjustments',
            icon: AdjustmentsHorizontalIcon
          }
        ]
      },
      {
        name: 'Operations',
        items: [
          {
            name: 'Devices',
            path: '/app/devices',
            icon: DevicePhoneMobileIcon
          }
        ]
      }
    ]
  } else if (actualUserRole === 'Administrator') {
    // Administrator navigation - show Contractors only if hasContractors is true
    const adminHasContractors = authStore.user?.company?.settings?.hasContractors === true || authStore.user?.company?.hasContractors === true
    
    const managementItems = [
      {
        name: 'Users',
        path: '/app/users',
        icon: UsersIcon
      }
    ]
    
    // Add Contractors if hasContractors is true
    if (adminHasContractors) {
      managementItems.push({
        name: 'Contractors',
        path: '/app/contractors',
        icon: UsersIcon
      })
    }
    
    // Add Locations and Tariffs
    managementItems.push(
      {
        name: 'Locations',
        path: '/app/locations',
        icon: MapPinIcon
      },
      {
        name: 'Tariffs',
        path: '/app/tariffs',
        icon: CreditCardIcon
      }
    )
    
    sections = [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          }
        ]
      },
      {
        name: 'Management',
        items: managementItems
      },
      {
        name: 'Operations',
        items: [
          {
            name: 'Transactions',
            path: '/app/transactions',
            icon: ChartBarIcon
          },
          {
            name: 'NFC Cards',
            path: '/app/nfc-cards',
            icon: IdentificationIcon
          },
          {
            name: 'NFC Subscriptions',
            path: '/app/card-subscriptions',
            icon: CreditCardIcon
          },
          {
            name: 'Devices',
            path: '/app/devices',
            icon: DevicePhoneMobileIcon
          }
        ]
      }
    ]
  } else if (userRole === 'Finance') {
    // Finance role navigation - Dashboard, Transactions
    sections = [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          }
        ]
      },
      {
        name: 'Operations',
        items: [
          {
            name: 'Transactions',
            path: '/app/transactions',
            icon: ChartBarIcon
          }
        ]
      }
    ]
  } else if (['ParkAdmin', 'ParkOwner', 'ParkOperation'].includes(userRole)) {
    sections = [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          }
        ]
      },
      {
        name: 'Management',
        items: [
          {
            name: 'Users',
            path: '/app/users',
            icon: UsersIcon
          },
          {
            name: 'Locations',
            path: '/app/locations',
            icon: MapPinIcon
          },
          {
            name: 'Tariffs',
            path: '/app/tariffs',
            icon: CreditCardIcon
          }
        ]
      },
      {
        name: 'Operations',
        items: [
          {
            name: 'Transactions',
            path: '/app/transactions',
            icon: ChartBarIcon
          },
          {
            name: 'Devices',
            path: '/app/devices',
            icon: DevicePhoneMobileIcon
          }
        ]
      }
    ]
  } else {
    // Default navigation for other roles (PremierAdmin, etc.)
    sections = [
      {
        name: 'Main',
        items: [
          {
            name: 'Dashboard',
            path: '/app/dashboard',
            icon: HomeIcon
          }
        ]
      },
      {
        name: 'Management',
        items: [
          {
            name: 'Users',
            path: '/app/users',
            icon: UsersIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'Administrator', 'Contractor']
          },
          {
            name: 'Companies',
            path: '/app/companies',
            icon: BuildingOfficeIcon,
            roles: ['PremierAdmin', 'SuperAdmin']
          },
          {
            name: 'Permissions',
            path: '/app/permissions',
            icon: ShieldCheckIcon,
            roles: ['PremierAdmin', 'SuperAdmin']
          },
          {
            name: 'Vehicles',
            path: '/app/vehicles',
            icon: TruckIcon
          }
        ]
      },
      {
        name: 'Accounting',
        items: [
          {
            name: 'Accounting Dashboard',
            path: '/app/accounting',
            icon: CalculatorIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Suppliers',
            path: '/app/accounting/suppliers',
            icon: BuildingStorefrontIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Stock Items',
            path: '/app/accounting/stock-items',
            icon: CubeIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Stock Batches',
            path: '/app/accounting/stock-batches',
            icon: RectangleStackIcon
          },
          {
            name: 'Purchase Orders',
            path: '/app/accounting/purchase-orders',
            icon: ShoppingCartIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Quotations',
            path: '/app/accounting/quotations',
            icon: DocumentTextIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Invoices',
            path: '/app/accounting/invoices',
            icon: ReceiptPercentIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Payment Plans',
            path: '/app/accounting/payment-plans',
            icon: DocumentTextIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Tax Settings',
            path: '/app/accounting/tax-settings',
            icon: CalculatorIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Quality Standards',
            path: '/app/accounting/quality-standards',
            icon: BeakerIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          },
          {
            name: 'Stock Adjustments',
            path: '/app/accounting/stock-adjustments',
            icon: AdjustmentsHorizontalIcon,
            roles: ['PremierAdmin', 'SuperAdmin', 'AdminAccountant']
          }
        ]
      },
      {
        name: 'Operations',
        items: [
          {
            name: 'Topups',
            path: '/app/topups',
            icon: CreditCardIcon
          },
          {
            name: 'Loyalty Cards',
            path: '/app/loyalty-cards',
            icon: IdentificationIcon
          },
          {
            name: 'Card Requests',
            path: '/app/loyalty-card-requests',
            icon: ClipboardDocumentListIcon
          },
          {
            name: 'Subscriptions',
            path: '/app/subscriptions',
            icon: DocumentTextIcon
          },
          {
            name: 'NFC Cards',
            path: '/app/nfc-cards',
            icon: IdentificationIcon
          },
          {
            name: 'NFC Subscriptions',
            path: '/app/card-subscriptions',
            icon: CreditCardIcon
          },
          {
            name: 'Devices',
            path: '/app/devices',
            icon: DevicePhoneMobileIcon
          }
        ]
      }
    ]
  }

  // Filter items based on user role for default navigation
  return sections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (!item.roles) return true
      return item.roles.includes(userRole) || item.roles.includes(actualUserRole)
    })
  })).filter(section => section.items.length > 0) // Remove empty sections
})

// Methods
const isActive = (path) => {
  return route.path === path || route.path.startsWith(path + '/')
}

const handleExitContractorView = () => {
  try {
    const stored = localStorage.getItem('contractorViewMode')
    if (stored) {
      const contractorViewData = JSON.parse(stored)
      
      // Restore original user data
      if (contractorViewData.originalUser) {
        authStore.user = contractorViewData.originalUser
        localStorage.setItem('user', JSON.stringify(contractorViewData.originalUser))
      }
      
      if (contractorViewData.originalToken) {
        authStore.token = contractorViewData.originalToken
        localStorage.setItem('token', contractorViewData.originalToken)
      }
    }
    
    // Remove contractor view mode
    localStorage.removeItem('contractorViewMode')
    
    router.push('/app/contractors')
    toast.success('Exited contractor view mode')
  } catch (error) {
    console.error('Error exiting contractor view:', error)
    toast.error('Failed to exit contractor view')
  }
}

const handleExitCompanyView = () => {
  authStore.clearCompanyViewMode()
  router.push('/app/companies')
  toast.success('Exited company view mode')
}

const handleLogout = async () => {
  try {
    // Clear contractor view mode if exists
    localStorage.removeItem('contractorViewMode')
    
    await authStore.logout()
    router.push('/login')
    toast.success('Logged out successfully')
  } catch (error) {
    toast.error('Logout failed')
  }
}
</script>

<style scoped>
/* Custom scrollbar for sidebar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Nav item base */
.nav-item {
  border-right: 3px solid transparent;
  position: relative;
}

/* Active pill state â€” light mode */
.nav-item-active {
  background: linear-gradient(to right, rgba(164, 255, 7, 0.14), rgba(164, 255, 7, 0.04));
  border-right-color: #a4ff07;
  box-shadow: 0 0 14px rgba(164, 255, 7, 0.14);
}

/* Active pill state â€” dark mode */
.dark .nav-item-active {
  background: linear-gradient(to right, rgba(164, 255, 7, 0.10), rgba(164, 255, 7, 0.02));
  border-right-color: #a4ff07;
  box-shadow: 0 0 14px rgba(164, 255, 7, 0.10);
}

/* Hover state â€” light mode */
.nav-item-hover:hover {
  background: rgba(164, 255, 7, 0.07);
  border-right-color: rgba(164, 255, 7, 0.35);
  box-shadow: 0 0 8px rgba(164, 255, 7, 0.06);
}

/* Hover state â€” dark mode */
.dark .nav-item-hover:hover {
  background: rgba(164, 255, 7, 0.07);
  border-right-color: rgba(164, 255, 7, 0.35);
  box-shadow: 0 0 8px rgba(164, 255, 7, 0.06);
}
</style>
